import { useContext } from "react"
import { string, object } from "prop-types"
import Edit from "source/registrations/reservations/Edit"
import { AlertMessage } from "source/shared/AlertMessage"
import {
  createResource,
  useCacheRead,
} from "source/shared/react-cache-but-its-a-context"
import { api } from "source/registrations/api/client"
import { routes, buildPath } from "source/shared/apiRoutes"
import { ReservationContext } from "source/registrations/reservations/ReservationContext"
import { useApiReadEntireCollection } from "source/shared/SessionApiResource"
import { transformResponse } from "@planningcenter/api-client"

const DEFAULT_PEOPLE_MAX_HOUSEHOLD_SIZE = 20

const ReservationResource = createResource(
  (routes) =>
    Promise.all(routes.map(([path, params]) => api.get(path, params))),
  (_paths) => "ReservationResource",
)

const Reservation = ({ session, reservationUuid, eventSignupPath }) => {
  const {
    event: {
      data: { id: eventId },
    },
  } = useContext(ReservationContext)

  const personId = session.id

  const [organization, registrantQuestions, households] = useCacheRead(
    ReservationResource,
    [
      buildPath(routes.registrations.organization),
      buildPath(routes.registrations.questions, {
        eventId,
        filter: "for_registrant",
      }),
      ...(personId
        ? [
            buildPath(routes.people.households, {
              personId,
              include:
                "non_pending_and_not_deceased_people,non_pending_and_not_deceased_people.phone_numbers",
            }),
          ]
        : []),
    ],
  )

  const addOns = useApiReadEntireCollection(
    `/registrations/v2/events/${eventId}/add_ons?order=position&per_page=100&include=add_on_variations`,
  ).reduce((addOns, page) => {
    addOns.push(...transformResponse(page).data)
    return addOns
  }, [])

  const attendeeTypes = useApiReadEntireCollection(
    `/registrations/v2/events/${eventId}/attendee_types?order=position&per_page=100`,
  ).reduce((acc, page) => {
    acc.push(...transformResponse(page).data)
    return acc
  }, [])

  if (
    organization?.errors ||
    households?.errors ||
    addOns?.errors ||
    registrantQuestions?.errors
  ) {
    return (
      <AlertMessage>
        Something went wrong, please try reloading the page or contact support.
      </AlertMessage>
    )
  }

  return (
    <Edit
      reservationUuid={reservationUuid}
      basePath={`/registrations/${eventSignupPath}/${eventId}/reservations`}
      organization={organization.data}
      addOns={addOns}
      registrantQuestions={registrantQuestions.data}
      attendeeTypes={attendeeTypes}
      households={households?.data || []}
      maxHouseholdSize={
        households?.meta?.maxHouseholdSize || DEFAULT_PEOPLE_MAX_HOUSEHOLD_SIZE
      }
      session={session}
    />
  )
}

Reservation.propTypes = {
  session: object.isRequired,
  reservationUuid: string,
  eventSignupPath: string,
}

export default Reservation
